// InlineImageBuilder — the per-explanation "Make image" experience,
// rendered INSIDE the chat (no modal). Mounts as a virtual turn directly
// under the assistant bubble the student tapped.
//
// Flow: the student types their OWN prompt. They may optionally click
// "Enhance prompt" — that (and only that) hits the build-image-prompt API,
// which enhances their prompt using the lesson content AND checks that the
// prompt is relevant to the lesson. If it's off-topic the API blocks it and
// we surface the message instead of generating. Once they click Generate,
// the panel debits 3 (or 8) credits, swaps the image in, and unmounts.
//
// The unmount-on-success is critical for the UX the user asked for:
// "if generated vanish that pop". Parent owns the lifetime — when the
// API returns, parent unmounts us and adds an image turn to history.
import React, { useState } from 'react';
import {
  Image as ImageIcon, Sparkles, Zap, X, Wand2,
} from 'lucide-react';
import api from '../../studentApi';

const KIND_META = {
  image:         { label: 'Picture',          tint: '#3B82F6' },
  flowchart:     { label: 'Flowchart',        tint: '#8B5CF6' },
  block_diagram: { label: 'Block diagram',    tint: '#0EA5E9' },
  mindmap:       { label: 'Mind map',         tint: '#22C55E' },
  chart:         { label: 'Chart',            tint: '#F59E0B' },
  sketch:        { label: 'Sketch',           tint: '#EC4899' },
  photo:         { label: 'Realistic',        tint: '#10B981' },
};
const KIND_KEYS = Object.keys(KIND_META);

export default function InlineImageBuilder({
  lessonId,
  sessionId,
  explanationText,   // assistant turn's text — the "context" for the image
  balance,           // live super-credits balance from parent
  onGenerated,       // ({ image, cost, new_balance, turn_id, session_id }) => void  — parent unmounts us
  onCancel,          // () => void — parent unmounts us
  onInsufficient,    // ({ needed, balance, reason }) => void — pops recharge sheet
}) {
  const [kind, setKind] = useState('image');
  const [prompt, setPrompt] = useState('');
  const [quality, setQuality] = useState('flash');
  const [enhancing, setEnhancing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  // Lesson-relevance rejection message from the server. When set, the prompt
  // is off-topic and generation is blocked until the student edits it.
  const [blocked, setBlocked] = useState(null);

  const meta = KIND_META[kind] || KIND_META.image;
  const cost = quality === 'flash' ? 3 : 8;
  const canAfford = (balance ?? 0) >= cost;
  const busy = enhancing || generating;

  const onPromptChange = (e) => {
    setPrompt(e.target.value);
    if (blocked) setBlocked(null);
    if (error) setError(null);
  };

  // Enhance the student's typed prompt using the lesson content. Cheap —
  // runs on Gemini and costs the student no credits. Fires ONLY on click.
  // The API also judges lesson-relevance and can block an off-topic prompt.
  const enhancePrompt = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setEnhancing(true);
    setError(null);
    setBlocked(null);
    try {
      const r = await api.post(`/student/tutor/lesson/${lessonId}/build-image-prompt`, {
        explanation_text: explanationText,
        user_prompt: p,
        suggested_kind: kind,
      });
      if (r.blocked) {
        setBlocked(r.reason || 'Your prompt must relate to this lesson.');
        return;
      }
      if (r.image_prompt) setPrompt(r.image_prompt);
      if (r.kind && KIND_META[r.kind]) setKind(r.kind);
    } catch (e) {
      setError('Could not enhance the prompt — you can still generate as-is.');
    } finally {
      setEnhancing(false);
    }
  };

  const handleGenerate = async () => {
    if (busy || !canAfford || !prompt.trim() || blocked) return;
    setGenerating(true);
    setError(null);
    try {
      const r = await api.post(`/student/tutor/lesson/${lessonId}/generate-image`, {
        session_id: sessionId,
        student_message: prompt.slice(0, 200),
        prompt,
        kind,
        quality,
        explanation_text: explanationText.slice(0, 1200),
      });
      onGenerated && onGenerated({
        turn_id: r.turn_id,
        session_id: r.session_id,
        image: r.image,
        cost: r.cost,
        new_balance: r.super_credits_remaining,
      });
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const detail = e?.response?.data?.detail || e?.detail;
      if (status === 402 && detail && typeof detail === 'object') {
        onInsufficient && onInsufficient({
          needed: detail.needed,
          balance: detail.balance,
          reason: `Generating a ${kind.replace('_', ' ')}`,
        });
      } else if (status === 422) {
        const msg = (detail && typeof detail === 'object' ? detail.reason : detail)
          || 'Your prompt must relate to this lesson.';
        setBlocked(msg);
      } else {
        setError('Image generation failed. Your credits were refunded.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex justify-start w-full" data-testid="inline-image-builder">
      <div
        className="w-full rounded-2xl px-3 py-3 flex flex-col gap-2.5 shadow-sm"
        style={{
          background: `color-mix(in srgb, ${meta.tint} 7%, var(--st-card, #fff))`,
          border: `1.5px solid color-mix(in srgb, ${meta.tint} 40%, transparent)`,
          color: 'var(--st-ink, #0f172a)',
        }}>
        {/* Header */}
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0"
            style={{ background: meta.tint }}>
            <Wand2 className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-extrabold leading-tight">
              Make this explanation into a {meta.label.toLowerCase()}
            </div>
            <div className="text-[10px] opacity-70 mt-0.5">
              Type a prompt, then Generate — or Enhance it first.
            </div>
          </div>
          <button
            onClick={onCancel}
            data-testid="inline-builder-cancel"
            disabled={generating}
            className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-black/10 disabled:opacity-40 shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Kind picker */}
        <div className="flex gap-1 flex-wrap">
          {KIND_KEYS.map(k => (
            <button
              key={k}
              onClick={() => setKind(k)}
              disabled={busy}
              data-testid={`inline-builder-kind-${k}`}
              className="px-2 py-0.5 rounded-full text-[10px] font-extrabold transition-all disabled:opacity-50"
              style={{
                background: kind === k ? KIND_META[k].tint : 'transparent',
                color: kind === k ? '#fff' : 'inherit',
                border: `1.5px solid ${kind === k ? KIND_META[k].tint : 'var(--st-border, #e5e7eb)'}`,
              }}>
              {KIND_META[k].label}
            </button>
          ))}
        </div>

        {/* Prompt — the student types their own, optionally enhances it */}
        <div>
          <div className="text-[9px] uppercase tracking-widest font-extrabold opacity-70 mb-0.5 flex items-center justify-between">
            <span>Prompt</span>
            <button
              onClick={enhancePrompt}
              disabled={busy || !prompt.trim()}
              data-testid="inline-builder-enhance"
              title="Enhance with AI using this lesson"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-black/5 disabled:opacity-40 normal-case tracking-normal">
              <Wand2 className={`w-2.5 h-2.5 ${enhancing ? 'animate-pulse' : ''}`} />
              {enhancing ? 'Enhancing…' : 'Enhance prompt'}
            </button>
          </div>
          <textarea
            value={prompt}
            onChange={onPromptChange}
            rows={3}
            placeholder="Describe the picture you want…"
            data-testid="inline-builder-prompt"
            className="w-full text-[11.5px] leading-snug px-2 py-1.5 rounded-lg outline-none resize-none"
            style={{
              background: 'var(--st-bg, #f8fafc)',
              border: '1px solid var(--st-border, #e5e7eb)',
              color: 'var(--st-ink, #0f172a)',
              maxHeight: 120,
            }} />
        </div>

        {blocked && (
          <div
            className="text-[10px] font-bold px-2 py-1.5 rounded leading-snug"
            style={{ background: 'color-mix(in srgb, #f59e0b 14%, transparent)', color: '#92400e' }}
            data-testid="inline-builder-blocked">
            {blocked}
          </div>
        )}

        {error && (
          <div
            className="text-[10px] font-bold px-2 py-1 rounded"
            style={{ background: 'color-mix(in srgb, #ef4444 12%, transparent)', color: '#b91c1c' }}
            data-testid="inline-builder-error">
            {error}
          </div>
        )}

        {/* Quality + Generate */}
        <div className="flex items-center gap-2 mt-0.5">
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => setQuality('flash')}
              disabled={generating}
              data-testid="inline-builder-flash"
              className="px-2 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 transition-all"
              style={{
                background: quality === 'flash' ? meta.tint : 'transparent',
                color: quality === 'flash' ? '#fff' : 'inherit',
                border: `1px solid ${quality === 'flash' ? meta.tint : 'var(--st-border, #e5e7eb)'}`,
              }}>
              <Zap className="w-2.5 h-2.5" /> Flash · 3cr
            </button>
            <button
              onClick={() => setQuality('pro')}
              disabled={generating}
              data-testid="inline-builder-pro"
              className="px-2 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 transition-all"
              style={{
                background: quality === 'pro' ? meta.tint : 'transparent',
                color: quality === 'pro' ? '#fff' : 'inherit',
                border: `1px solid ${quality === 'pro' ? meta.tint : 'var(--st-border, #e5e7eb)'}`,
              }}>
              <Sparkles className="w-2.5 h-2.5" /> Pro · 8cr
            </button>
          </div>
          <div className="flex-1" />
          <button
            onClick={handleGenerate}
            disabled={busy || !canAfford || !prompt.trim() || !!blocked}
            data-testid="inline-builder-generate"
            className="px-3 py-1.5 rounded-full text-[11px] font-extrabold text-white inline-flex items-center gap-1 transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:translate-y-0 shrink-0"
            style={{ background: meta.tint }}>
            <ImageIcon className="w-3 h-3" />
            {generating ? (
              <span className="inline-flex gap-0.5 ml-1">
                <span className="w-1 h-1 rounded-full bg-white animate-bounce" />
                <span className="w-1 h-1 rounded-full bg-white animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1 h-1 rounded-full bg-white animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
            ) : <>Generate · {cost}cr</>}
          </button>
        </div>

        {!canAfford && !generating && (
          <div
            className="text-[10px] font-bold px-2 py-1 rounded"
            style={{ background: 'color-mix(in srgb, #ef4444 12%, transparent)', color: '#b91c1c' }}
            data-testid="inline-builder-shortfall">
            Need {cost - (balance ?? 0)} more credits. Tap the credits pill in the header to top up.
          </div>
        )}
      </div>
    </div>
  );
}
